/**
 * SHEIN 关联属性填写规则 get-associated-attribute-rules。
 * 入参只传已填商品属性（勿传销售/尺码）；结果中的 attribute_id 均为必填，
 * 且可能带属性值可选范围（value_list ∪ pre_fill_list）。
 */

import { attributeDisplayLabel } from '@/shein-platform/utils/sheinAttributeTemplate';

function fail(message, section = 'attrs', field = 'productAttributes') {
  return { ok: false, message, section, field };
}

function unwrapInfo(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.info != null) return raw.info;
  return raw;
}

/** 只收集商品属性（productAttributes），不传销售/尺码 */
export function collectProductAttributePairs(form = {}) {
  const pairs = [];
  const attrs = form.productAttributes || {};
  Object.entries(attrs).forEach(([attributeId, value]) => {
    const aid = Number(attributeId);
    if (!Number.isFinite(aid) || aid <= 0) return;
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((row) => {
        if (row && typeof row === 'object') {
          const valueId = Number(row.valueId ?? row.attributeValueId);
          if (Number.isFinite(valueId) && valueId > 0) {
            pairs.push({ attribute_id: aid, attribute_value_id: valueId });
          }
          return;
        }
        const valueId = Number(row);
        if (Number.isFinite(valueId) && valueId > 0) {
          pairs.push({ attribute_id: aid, attribute_value_id: valueId });
        }
      });
      return;
    }
    if (typeof value === 'object') {
      const valueId = Number(value.valueId ?? value.attributeValueId);
      if (Number.isFinite(valueId) && valueId > 0) {
        pairs.push({ attribute_id: aid, attribute_value_id: valueId });
      }
      return;
    }
    const valueId = Number(value);
    if (Number.isFinite(valueId) && valueId > 0) {
      pairs.push({ attribute_id: aid, attribute_value_id: valueId });
    }
  });
  return pairs;
}

/** 商品属性 + 销售/尺码（结果侧可能要求尺码属性） */
export function isAttributeFilled(form, attributeId) {
  const id = String(attributeId);
  const val = form?.productAttributes?.[id];
  if (val != null && val !== '') {
    if (Array.isArray(val)) {
      const filled = val.some((row) => {
        if (row && typeof row === 'object') return Boolean(row.valueId ?? row.attributeValueId);
        return row != null && String(row).trim() !== '';
      });
      if (filled) return true;
    } else if (typeof val === 'object') {
      if (Boolean(val.valueId ?? val.attributeValueId ?? val.attributeExtraValue)) return true;
    } else if (String(val).trim() !== '') {
      return true;
    }
  }
  for (const skc of form.skcList || []) {
    if (String(skc?.colorAttributeId || '') === id
      && (skc.colorValueId || skc.colorExtraValue || skc.colorLabel)) {
      return true;
    }
    for (const sku of skc?.skuRows || []) {
      if (String(sku?.sizeAttributeId || '') === id && (sku.sizeValueId || sku.sizeExtraValue)) {
        return true;
      }
    }
  }
  // 尺码表（attribute_type=2）格子
  const chart = form.sizeChart || {};
  for (const row of Object.values(chart)) {
    if (!row || typeof row !== 'object') continue;
    if (String(row[id] ?? '').trim()) return true;
  }
  return false;
}

export function attrNameById(attrTpl, attributeId) {
  const id = String(attributeId);
  const pool = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.mainAttrs || []),
    ...(attrTpl?.otherAttrs || []),
    ...(attrTpl?.sizeAttrs || []),
  ];
  const hit = pool.find((a) => String(a.attributeId) === id);
  return attributeDisplayLabel(hit) || `属性 ${id}`;
}

function collectSelectedValueIds(form, attributeId) {
  const val = form.productAttributes?.[String(attributeId)];
  const selectedIds = [];
  if (Array.isArray(val)) {
    val.forEach((item) => {
      if (item && typeof item === 'object') {
        const id = item.valueId ?? item.attributeValueId;
        if (id != null) selectedIds.push(String(id));
      } else if (item != null && String(item).trim() !== '') {
        selectedIds.push(String(item));
      }
    });
  } else if (val && typeof val === 'object') {
    const id = val.valueId ?? val.attributeValueId;
    if (id != null) selectedIds.push(String(id));
  } else if (val != null && String(val).trim() !== '') {
    selectedIds.push(String(val));
  }
  return selectedIds;
}

/**
 * @returns {Map<string, { attributeId: string, allowedValueIds: string[] | null }>}
 */
export function parseAssociatedAttributeRules(raw) {
  const info = unwrapInfo(raw);
  let data = info;
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    data = info.data || info.list || [];
  }
  const group = Array.isArray(data) ? (data[0] || {}) : {};
  const linked = group.link_rule_attribute_list
    || group.linkRuleAttributeList
    || [];
  const map = new Map();
  (Array.isArray(linked) ? linked : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const attributeId = row.attribute_id ?? row.attributeId;
    if (attributeId == null || attributeId === '') return;
    const id = String(attributeId);
    const allowed = [
      ...(Array.isArray(row.attribute_value_list) ? row.attribute_value_list : []),
      ...(Array.isArray(row.attribute_value_pre_fill_list) ? row.attribute_value_pre_fill_list : []),
      ...(Array.isArray(row.attributeValueList) ? row.attributeValueList : []),
      ...(Array.isArray(row.attributeValuePreFillList) ? row.attributeValuePreFillList : []),
    ].map((v) => String(v)).filter(Boolean);
    const prev = map.get(id);
    if (!prev) {
      map.set(id, {
        attributeId: id,
        allowedValueIds: allowed.length ? [...new Set(allowed)] : null,
      });
      return;
    }
    // 同 attribute_id 多条：合并范围；任一为 null（无范围）则保持仅必填
    if (prev.allowedValueIds == null || !allowed.length) {
      map.set(id, { attributeId: id, allowedValueIds: null });
    } else {
      map.set(id, {
        attributeId: id,
        allowedValueIds: [...new Set([...prev.allowedValueIds, ...allowed])],
      });
    }
  });
  return map;
}

/**
 * @param {object} form
 * @param {Map|object|null} rulesMap parseAssociatedAttributeRules 结果或 plain object
 * @param {object|null} attrTpl
 * @param {{ satisfiedSiblingIds?: Set<string>|Array<string> }} [options]
 *   同名必填组（如两个「主料克重」）内已有其他成员填写时，本项不再单独要求必填，
 *   但仍校验已填值是否落在平台允许范围内。
 */
export function validateLinkedAttributeRules(form, rulesMap, attrTpl = null, options = {}) {
  const map = rulesMap instanceof Map
    ? rulesMap
    : (rulesMap && typeof rulesMap === 'object'
      ? new Map(Object.entries(rulesMap).map(([k, v]) => [String(k), v]))
      : new Map());
  if (!map.size) return { ok: true };

  const satisfiedSiblingIds = options?.satisfiedSiblingIds instanceof Set
    ? options.satisfiedSiblingIds
    : new Set((Array.isArray(options?.satisfiedSiblingIds) ? options.satisfiedSiblingIds : []).map(String));

  for (const [attributeId, rule] of map.entries()) {
    if (!isAttributeFilled(form, attributeId) && !satisfiedSiblingIds.has(String(attributeId))) {
      const name = attrNameById(attrTpl, attributeId);
      return fail(`关联属性规则要求必填：${name}`, 'attrs', 'productAttributes');
    }
    const allowed = rule?.allowedValueIds;
    if (!Array.isArray(allowed) || !allowed.length) continue;
    const selectedIds = collectSelectedValueIds(form, attributeId);
    // 尺码表等无 productAttributes 值 id 时跳过范围校验（仅要求有填）
    if (!selectedIds.length) continue;
    const invalid = selectedIds.filter((id) => id && !allowed.includes(id));
    if (invalid.length) {
      const name = attrNameById(attrTpl, attributeId);
      return fail(`关联属性「${name}」所选属性值不在平台允许范围内`, 'attrs', 'productAttributes');
    }
  }
  return { ok: true };
}

/** 从原始响应直接校验（预检用） */
export function validateAssociatedRulesRaw(form, rulesRaw, attrTpl = null) {
  return validateLinkedAttributeRules(form, parseAssociatedAttributeRules(rulesRaw), attrTpl);
}

/** 判断属性值是否仍在允许范围内；无范围则恒 true */
export function isValueAllowedByLinkedRule(value, allowedValueIds) {
  if (!Array.isArray(allowedValueIds) || !allowedValueIds.length) return true;
  const allowed = new Set(allowedValueIds.map(String));
  if (value == null || value === '') return true;
  if (Array.isArray(value)) {
    return value.every((item) => {
      if (item && typeof item === 'object') {
        const id = item.valueId ?? item.attributeValueId;
        if (id == null || id === '') return true;
        return allowed.has(String(id));
      }
      if (item == null || String(item).trim() === '') return true;
      return allowed.has(String(item));
    });
  }
  if (typeof value === 'object') {
    const id = value.valueId ?? value.attributeValueId;
    if (id == null || id === '') return true;
    return allowed.has(String(id));
  }
  return allowed.has(String(value));
}
